import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack, TextField, InputAdornment, Checkbox, FormControlLabel, Chip, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import dateFormats from '../../services/dateFormats';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

export default function ATKRegistrations() {
  const { t } = useTranslation();
  const [workers, setWorkers]             = useState([]);
  const [q, setQ]                         = useState('');
  const [onlyUnregistered, setOnlyUnreg] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [editWorker, setEditWorker]       = useState(null);
  const [editDate, setEditDate]           = useState('');
  const [saving, setSaving]               = useState(false);
  const [toast, setToast]                 = useState('');
  const [emailOpen, setEmailOpen]         = useState(false);

  const pdfCols = [
    { label: t('report.nr'), key: (r, i) => i + 1, align: 'left', width: '5%' },
    { label: t('report.fullName'), key: 'fullName', align: 'left', width: '30%' },
    { label: t('report.nidLabel'), key: 'nid', align: 'left', width: '20%' },
    { label: t('report.employmentDate'), key: 'hireDate', align: 'left', width: '15%' },
    { label: t('report.atkRegistrationDate'), key: 'atkDate', align: 'left', width: '15%' },
    { label: t('report.status'), key: 'status', align: 'left', width: '15%' },
  ];

  const getATKDate = (w) =>
    w?.ATKRegisteredAt || w?.atkRegisteredAt || w?.AtkRegisteredAt || '';
  const isConfirmed = (w) => {
    const v = w?.ATKRegistered ?? w?.atkRegistered ?? w?.AtkRegistered;
    if (v === true) return true;
    if (v === false) return false;
    if (typeof v === 'number') return v === 1;
    return ['1','true','po','yes'].includes(String(v || '').toLowerCase());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService().get('workers');
      if (r?.success) setWorkers(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = workers || [];
    if (onlyUnregistered) list = list.filter(w => !getATKDate(w) || !isConfirmed(w));
    if (q) {
      const term = q.toLowerCase();
      list = list.filter(w =>
        [w.FirstName, w.LastName, w.PersonalNumber, w.PersonalId, w.Email]
          .filter(Boolean).map(String).map(s => s.toLowerCase())
          .some(s => s.includes(term))
      );
    }
    return list;
  }, [workers, q, onlyUnregistered]);

  const registered   = useMemo(() => workers.filter(w => isConfirmed(w) && getATKDate(w)), [workers]);
  const unregistered = useMemo(() => workers.filter(w => !isConfirmed(w) || !getATKDate(w)), [workers]);

  const openEdit = (w) => {
    setEditWorker(w);
    setEditDate(getATKDate(w) || new Date().toISOString().slice(0, 10));
  };

  const handleSave = async () => {
    if (!editWorker || !editDate) return;
    setSaving(true);
    try {
      const updated = {
        ...editWorker,
        ATKRegistered: true,
        ATKRegisteredAt: editDate,
      };
      await apiService().put('workers', updated);
      setWorkers(prev => prev.map(w => Number(w.Id) === Number(editWorker.Id) ? updated : w));
      setToast(t('report.registrationSaved'));
      setEditWorker(null);
    } finally {
      setSaving(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  const pdfRows = filtered.map((w, i) => ({
    nr: i + 1,
    fullName: `${w.FirstName || ''} ${w.LastName || ''}`.trim(),
    nid: w.PersonalNumber || w.PersonalId || '—',
    hireDate: dateFormats(w.HireDate) || '—',
    atkDate: getATKDate(w) ? dateFormats(getATKDate(w)) : '—',
    status: isConfirmed(w) && getATKDate(w) ? t('report.registered') : t('report.notRegistered'),
  }));

  const exportPdf = () => printReport({
    title: t('reports.atkRegistrations'),
    subtitle: `${new Date().toLocaleDateString('sq-AL')}`,
    orientation: 'landscape',
    columns: pdfCols,
    rows: pdfRows,
    badge: { label: `${registered.length} regjistruar / ${unregistered.length} pa regjistruar` },
  });

  const exportCsv = () => {
    const headers = [t('report.nr'), t('report.name'), t('report.name'), t('report.nidLabel'), t('report.atkRegistrationDate'), t('report.status')];
    const lines = [headers.join(',')];
    filtered.forEach((w, i) => {
      const row = [
        i + 1,
        w.FirstName || '',
        w.LastName || '',
        w.PersonalNumber || w.PersonalId || '',
        getATKDate(w) || '',
        isConfirmed(w) ? t('report.registered') : t('report.notRegistered'),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(row.join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `atk_registrations_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.atkRegistrations')}
        actions={<>
          <ExportMenu onCsv={exportCsv} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} />
        </>}
      />

      {toast && <Alert severity="success" sx={{ mb: 2 }}>{toast}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Chip label={`${t('report.registered')}: ${registered.length}`}   color="success" variant="outlined" icon={<CheckCircleIcon />} />
        <Chip label={`${t('report.notRegistered')}: ${unregistered.length}`} color="error"   variant="outlined" icon={<CancelIcon />} />
        <Chip label={`${t('report.total')}: ${workers.length}`} color="default" variant="outlined" />
      </Stack>

      {unregistered.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{t('report.urgentActionNeeded')}:</strong> {t('report.unregisteredAlert', { count: unregistered.length })}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small" placeholder="Kërko sipas emrit, NID..."
              value={q} onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 250 }}
            />
            <FormControlLabel
              control={<Checkbox checked={onlyUnregistered} onChange={(e) => setOnlyUnreg(e.target.checked)} />}
              label={t('report.onlyUnregistered')}
            />
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('report.nr')}</TableCell>
                <TableCell>{t('report.fullName')}</TableCell>
                <TableCell>{t('report.nidLabel')}</TableCell>
                <TableCell>{t('report.employmentDate')}</TableCell>
                <TableCell>{t('report.atkRegistrationDate')}</TableCell>
                <TableCell>{t('report.status')}</TableCell>
                <TableCell>{t('report.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center">{t('report.loading')}</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">{t('report.noResults')}</TableCell></TableRow>
              ) : filtered.map((w, i) => {
                const confirmed = isConfirmed(w);
                const atkDate   = getATKDate(w);
                return (
                  <TableRow key={w.Id}
                    sx={{ bgcolor: !confirmed ? 'rgba(239,68,68,0.04)' : undefined }}
                  >
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{w.FirstName} {w.LastName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {w.PersonalNumber || w.PersonalId || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{dateFormats(w.HireDate) || '—'}</TableCell>
                    <TableCell>{atkDate ? dateFormats(atkDate) : '—'}</TableCell>
                    <TableCell>
                      {confirmed && atkDate
                        ? <Chip size="small" color="success" label={t('report.registered')} icon={<CheckCircleIcon />} />
                        : <Chip size="small" color="error"   label={t('report.notRegistered')} icon={<CancelIcon />} />
                      }
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('report.atkDialogTitle')}>
                        <IconButton size="small" onClick={() => openEdit(w)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.atkRegistrations')}
        subtitle={new Date().toLocaleDateString('sq-AL')}
        columns={pdfCols} rows={pdfRows}
        workers={workers}
        orientation="landscape"
      />

      <Dialog open={!!editWorker} onClose={() => setEditWorker(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('report.atkDialogTitle')} — {editWorker?.FirstName} {editWorker?.LastName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            NID: <strong>{editWorker?.PersonalNumber || editWorker?.PersonalId || '—'}</strong>
          </Typography>
          <TextField
            fullWidth type="date" label={t('report.atkRegistrationDate')}
            InputLabelProps={{ shrink: true }}
            value={editDate} onChange={(e) => setEditDate(e.target.value)}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('report.atkRegistrationSaveNote')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWorker(null)}>{t('report.cancelLabel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !editDate}>
            {saving ? t('report.saving') : t('report.confirmRegistration')}
          </Button>
        </DialogActions>
      </Dialog>
    </ReportPage>
  );
}
